import { useCallback, useState } from 'react'

import { fitsField, type SearchFilterField } from './definition'
import { draftOf, emptyDraft, type DraftFilter } from './FilterFieldEditor'
import { formatFilter, isComplete, type FieldFilter, type SearchState } from './search-state'

interface Drafts {
  /** Each field's filter as typed, whole or not. */
  readonly byField: ReadonlyMap<string, DraftFilter>
  /** The applied filters these drafts' own changes lead to, oldest first, until the address shows each. */
  readonly pending: readonly string[]
  /** The applied filters last seen. */
  readonly seen: string
  /** What the pending changes leave applied, for the next change to build on. */
  readonly latest: readonly FieldFilter[]
}

function keyOf(filters: readonly FieldFilter[]): string {
  return filters.map(formatFilter).join('\n')
}

/** The filters once one field's changes: the others, then its own if it is whole and valid. */
function withField(filters: readonly FieldFilter[], field: string, applied: FieldFilter | null): readonly FieldFilter[] {
  return [...filters.filter((other) => other.field !== field), ...(applied === null ? [] : [applied])]
}

/**
 * Each field's filter as the always-shown sidebar edits it, applied as it changes once it is whole
 * and valid. A draft not yet whole, such as a range with one end, stays as typed. When the applied
 * filters change any other way, as when a chip is removed, Clear All is pressed or a saved search
 * opens, every field shows them instead.
 */
export function useFilterDrafts(
  filters: readonly FieldFilter[],
  update: (change: (state: SearchState) => SearchState) => void,
) {
  const key = keyOf(filters)
  const [drafts, setDrafts] = useState<Drafts>(() => ({ byField: new Map(), pending: [], seen: key, latest: filters }))

  // Adjusted while rendering, as React advises for state that follows a prop.
  let current = drafts
  if (key !== drafts.seen) {
    const at = drafts.pending.indexOf(key)
    current =
      at === -1
        ? { byField: new Map(), pending: [], seen: key, latest: filters }
        : { ...drafts, pending: drafts.pending.slice(at + 1), seen: key }
    setDrafts(current)
  }
  const { byField } = current

  const draftFor = useCallback(
    (field: SearchFilterField): DraftFilter => {
      const draft = byField.get(field.id)
      if (draft !== undefined) {
        return draft
      }
      const applied = filters.find((filter) => filter.field === field.id)
      return applied === undefined ? emptyDraft(field) : draftOf(applied)
    },
    [byField, filters],
  )

  const change = useCallback(
    (field: SearchFilterField, draft: DraftFilter) => {
      const filter: FieldFilter = { field: field.id, ...draft }
      // Applied only once it is whole and valid, such as a number that is a number.
      const applied = isComplete(filter) && fitsField(field, filter) ? filter : null
      setDrafts((previous) => {
        const latest = withField(previous.pending.length === 0 ? filters : previous.latest, field.id, applied)
        const next = keyOf(latest)
        return {
          byField: new Map(previous.byField).set(field.id, draft),
          // An edit that applies nothing new, such as a draft not yet whole, changes no address.
          pending: next === (previous.pending.at(-1) ?? previous.seen) ? previous.pending : [...previous.pending, next],
          seen: previous.seen,
          latest,
        }
      })
      update((state) => ({ ...state, filters: withField(state.filters, field.id, applied) }))
    },
    [filters, update],
  )

  /** Forgets every draft, as Clear All does, even one that applied nothing. */
  const reset = useCallback(() => setDrafts((previous) => ({ ...previous, byField: new Map(), pending: [] })), [])

  return { draftFor, change, reset }
}
